import { array, object, string } from 'yup';
import { isValid } from 'date-fns';
import { injectable } from 'inversify';

import EpgService from '../EpgService';
import type { PlaylistItem } from '../../../types/playlist';
import type { EpgProgram } from '../../../types/epg';
import { getDataOrThrow } from '../../utils/api';
import { logError, logWarn } from '../../logger';

const AUTHENTICATION_HEADER = 'API-KEY';

const jwEpgProgramSchema = object().shape({
  id: string().required(),
  title: string().required(),
  startTime: string()
    .required()
    .test((value) => (value ? isValid(new Date(value)) : false)),
  endTime: string()
    .required()
    .test((value) => (value ? isValid(new Date(value)) : false)),
  chapterPointCustomProperties: array().of(
    object().shape({
      key: string().required(),
      value: string().test('required-but-empty', 'value is required', (value: unknown) => typeof value === 'string'),
    }),
  ),
});

@injectable()
export default class JWEpgService extends EpgService {
  /** JW schedules typically contain about a day of data. */
  epgDays = 1;

  transformProgram = async (data: unknown, item: PlaylistItem): Promise<EpgProgram> => {
    const program = await jwEpgProgramSchema.validate(data);
    const image = program.chapterPointCustomProperties?.find((item) => item.key === 'image')?.value || undefined;

    return {
      id: program.id,
      channelId: item.mediaid,
      title: program.title,
      startTime: program.startTime,
      endTime: program.endTime,
      cardImage: image,
      cardImageSmall: image,
      backgroundImage: image,
      description: program.chapterPointCustomProperties?.find((item) => item.key === 'description')?.value || undefined,
    };
  };

  fetchSchedule = async (item: PlaylistItem): Promise<EpgProgram[]> => {
    if (!item.scheduleUrl) {
      logWarn('JWEpgService', 'Tried requesting a schedule for an item with missing `scheduleUrl`', { item });
      return [];
    }

    const headers = new Headers();

    // add authentication token when `scheduleToken` is defined
    if (item.scheduleToken) {
      headers.set(AUTHENTICATION_HEADER, item.scheduleToken);
    }

    try {
      const response = await fetch(item.scheduleUrl, {
        headers,
      });

      // await needed to ensure the error is caught here
      const data = await getDataOrThrow(response);

      if (Array.isArray(data)) {
        const settled = await Promise.allSettled(data.map((program) => this.transformProgram(program, item)));

        return settled.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        logError('JWEpgService', `Fetch failed for EPG schedule: '${item.scheduleUrl}'`, { error });
      }
    }

    return [];
  };

  /** Each channel is resolved with its own schedule request; the range arguments are ignored. */
  fetchPrograms = async (channels: PlaylistItem[]) => {
    const result = await Promise.all(channels.map((channel) => this.fetchSchedule(channel)));

    return result.flat();
  };
}
